import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { get } from 'dpl/shared/utils/object';
import { sendAnalyticsEvent, sendAnalyticsIdentify } from 'dpl/util/analytics';
import postSignupRedirect from 'dpl/util/postSignupRedirect';
import { SIGNUP_FORM_TYPES, USER_TYPES } from 'dpl/shared/constants/shared';
import useForm from 'dpl/shared/form/hooks/useForm';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import { createUserMutationDefinition } from 'dpl/shared/buyer/utils/queryDefinitions';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import { SIGNUP_REF_ACTION } from 'dpl/auth/utils/constants';
import { ANALYTICS_EVENTS, ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import Input from 'dpl/common/design_system/Input';
import PasswordInput from 'dpl/common/design_system/PasswordInput';

import EmailVerificationForm from '../EmailVerificationForm';

interface SignupFormBuyerV2Props {
  email: string;
  onSubmit: (json?: any) => void;
  referredBy?: string | null;
}

function SignupFormBuyerV2({
  email,
  onSubmit,
  referredBy = null
}: SignupFormBuyerV2Props) {
  const { mutateAsync: signup } = useResourceMutation(
    createUserMutationDefinition
  );
  const {
    handleFieldChange,
    isSubmitting,
    didSubmissionFail,
    formState,
    formErrors,
    handleSubmission: handleFormSubmission
  } = useForm({
    onSubmit: async (fs: any, additionalAttributes: any) => {
      const res = await signup({
        body: {
          user_type: USER_TYPES.BUYER,
          user: {
            ...fs,
            ...additionalAttributes,
            referred_by: referredBy,
            email
          }
        }
      });

      return res;
    }
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const res = await handleFormSubmission();
    if (res.response.status === 200) {
      onSubmit();
    }
  }

  useEffect(() => {
    sendAnalyticsEvent(ANALYTICS_EVENTS.REGISTRATION_MODAL_VIEWED, {
      view: ANALYTICS_VIEWS.CREATE_ACCOUNT
    });
  }, []);

  return (
    <form onSubmit={handleSubmit} className="SignupForm">
      <div className="flex mb2">
        <div className="w-50 pr2">
          <label htmlFor="first_name">
            <p className="secondary font-12 mb2">First name</p>
            <ErrorWrapper
              errors={formErrors.first_name}
              isShown={didSubmissionFail}
            >
              <Input
                name="first_name"
                className="w-100"
                value={formState.first_name || ''}
                onChange={handleFieldChange}
                autoComplete="given-name"
                placeholder="First name"
              />
            </ErrorWrapper>
          </label>
        </div>
        <div className="w-50">
          <label htmlFor="last_name">
            <p className="secondary font-12 mb2">Last name</p>
            <ErrorWrapper
              errors={formErrors.last_name}
              isShown={didSubmissionFail}
            >
              <Input
                onChange={handleFieldChange}
                className="w-100"
                name="last_name"
                autoComplete="family-name"
                placeholder="Last name"
              />
            </ErrorWrapper>
          </label>
        </div>
      </div>
      <div className="mb2">
        <p className="secondary font-12 mb2">Email</p>
        <Input
          disabled
          name="email"
          className="w-100"
          value={email}
          autoCapitalize="none"
        />
      </div>

      <div className="mb6">
        <label htmlFor="password">
          <p className="secondary font-12 mb2">Password</p>
          <ErrorWrapper
            errors={formErrors.password}
            isShown={didSubmissionFail}
          >
            <PasswordInput
              onChange={handleFieldChange}
              name="password"
              label="Password"
              placeholder="Password"
              autoCapitalize="none"
            />
          </ErrorWrapper>
        </label>
      </div>

      <div className="mb6">
        <button
          data-test-id="signup_submit"
          type="submit"
          disabled={isSubmitting}
          className="button button--primary fw-medium pv3 w-100"
        >
          Create account
        </button>
      </div>
    </form>
  );
}

interface SignupFormV2Props {
  userType: string;
  referredBy?: string | null;
  onAuth?: ((json: any) => any) | null;
  onLoginClick: (e: React.MouseEvent) => void;
}

export default function SignupFormV2({
  userType,
  referredBy = null,
  onAuth = null,
  onLoginClick
}: SignupFormV2Props) {
  const queryParams = useSelector((state: any) => state.queryParams);
  const headerTitle = queryParams[SIGNUP_REF_ACTION]
    ? 'Create an account to continue'
    : 'Create an account';
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  function handleSubmit(json?: any) {
    sendAnalyticsIdentify({ userId: get(json, 'data.user.id') });

    if (onAuth) {
      return onAuth(json);
    }

    return postSignupRedirect(userType);
  }
  const stateData = {
    user_type: userType
  };

  return (
    <div className="SignupFormV2">
      <h1 className="md:font-32 font-28 lh-title pb8 tc">{headerTitle}</h1>
      {verifiedEmail ? (
        <SignupFormBuyerV2
          email={verifiedEmail}
          referredBy={referredBy}
          onSubmit={handleSubmit}
        />
      ) : (
        <>
          <EmailVerificationForm
            formType={SIGNUP_FORM_TYPES.SIGNUP}
            onEmailVerified={setVerifiedEmail}
            oauthState={stateData}
            analytics={{ view: ANALYTICS_VIEWS.CREATE_ACCOUNT_MODAL }}
          />
          <div className="pt6 mt6 tc bt b--light-gray secondary font-16">
            <span>Already have an account?</span>
            &nbsp;
            <button
              data-test-id="breeder_signup_button"
              className="stone underline"
              onClick={e => {
                e.preventDefault();
                onLoginClick(e);
              }}
              type="button"
            >
              Log in
            </button>
          </div>
        </>
      )}
    </div>
  );
}
